import React, { useState, useCallback } from 'react';
import {
  Container,
  Typography,
  Button,
  Paper,
  Box,
  Chip
} from '@mui/material';
import { Add as AddIcon, Business as BusinessIcon } from '@mui/icons-material';
import { useEntitySearch } from '../../hooks/useEntitySearch';
import { useNotification } from '../../context/NotificationContext';
import {
  searchCompanies,
  getCompany,
  createCompany,
  updateCompany,
  deleteCompany,
  batchEnrichCompanies
} from '../../api/companies';
import { Company, CompanyCreate, CompanyUpdate, CompanyListItem } from '../../types/company';
import SearchBar from '../../components/common/SearchBar/SearchBar';
import EntitySearchResults from '../../components/entities/EntitySearchResults/EntitySearchResults';
import CompanyForm from '../../components/entities/CompanyForm/CompanyForm';
import LoadingSpinner from '../../components/common/LoadingSpinner/LoadingSpinner';
import ErrorMessage from '../../components/common/ErrorMessage/ErrorMessage';

const CompaniesPage: React.FC = () => {
  const { showNotification } = useNotification();

  // Wrapper to extract results array from SearchCompaniesResult
  const searchCompaniesWrapper = async (query: string, limit?: number): Promise<CompanyListItem[]> => {
    const result = await searchCompanies(query, limit);
    return result.results;
  };

  const { results, loading: searchLoading, error: searchError, search, clearResults } = useEntitySearch<CompanyListItem>(searchCompaniesWrapper);

  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [loadingCompany, setLoadingCompany] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [enrichingBatch, setEnrichingBatch] = useState(false);

  const handleSearch = useCallback(async (query: string) => {
    await search(query);
  }, [search]);

  const handleSelectCompany = async (item: CompanyListItem) => {
    setLoadingCompany(true);
    setError(null);

    try {
      const company = await getCompany(item.id);
      setSelectedCompany(company);
      clearResults(); // Clear search results after selection
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to load company');
      showNotification('Failed to load company', 'error');
    } finally {
      setLoadingCompany(false);
    }
  };

  const handleNewCompany = () => {
    setSelectedCompany(null);
    setError(null);
  };

  const handleSave = async (data: CompanyCreate | CompanyUpdate, id?: string) => {
    try {
      if (id) {
        const updated = await updateCompany(id, data as CompanyUpdate);
        setSelectedCompany(updated);
        showNotification('Company updated successfully', 'success');
      } else {
        const created = await createCompany(data as CompanyCreate);
        setSelectedCompany(created);
        showNotification('Company created successfully', 'success');
      }
      // Refresh search results
      if (results.length > 0) {
        await search(results[0].name.substring(0, 3));
      }
    } catch (err: any) {
      throw err; // Let form handle the error
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteCompany(id);
      setSelectedCompany(null);
      showNotification('Company deleted successfully', 'success');
      // Refresh search results
      if (results.length > 0) {
        await search(results[0].name.substring(0, 3));
      }
    } catch (err: any) {
      throw err; // Let form handle the error
    }
  };

  const handleBulkEnrich = async () => {
    if (results.length === 0) return;

    setEnrichingBatch(true);
    showNotification(`Started processing ${results.length} companies. This might take a few minutes...`, 'info');
    try {
      const ids = results.map(r => r.id);
      await batchEnrichCompanies({ company_ids: ids });
      showNotification(`Successfully enriched ${ids.length} companies.`, 'success');
    } catch (err: any) {
      showNotification('Failed to batch enrich companies', 'error');
    } finally {
      setEnrichingBatch(false);
    }
  };

  const renderCompanyItem = (company: CompanyListItem) => (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <BusinessIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {company.name}
          </Typography>
        </Box>
        {company.ticker && (
          <Chip label={company.ticker} size="small" color="primary" variant="outlined" />
        )}
      </Box>
      {company.summary_description && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {company.summary_description.substring(0, 200)}
          {company.summary_description.length > 200 ? '...' : ''}
        </Typography>
      )}
      {company.corporate_status && (
        <Chip
          label={company.corporate_status}
          size="small"
          color={company.corporate_status === 'Active' ? 'success' : 'default'}
          sx={{ mt: 1 }}
        />
      )}
    </Box>
  );

  return (
    <Container maxWidth="xl" className="py-6">
      {/* Top Toolbar */}
      <Box className="flex items-center justify-between gap-4 mb-6">
        <Typography variant="h4" component="h1" className="font-bold text-gray-800 flex items-center gap-2">
          <BusinessIcon fontSize="large" className="text-blue-600" />
          Companies
        </Typography>

        <Box className="flex items-center gap-3 flex-1 max-w-2xl">
          <SearchBar
            onSearch={handleSearch}
            placeholder="Search companies by name..."
          />
          {results.length > 0 && (
            <Button
              variant="outlined"
              color="primary"
              onClick={handleBulkEnrich}
              disabled={enrichingBatch}
              size="large"
              className="whitespace-nowrap"
            >
              {enrichingBatch ? 'Enriching...' : 'Bulk Enrich Visible'}
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleNewCompany}
            size="large"
            className="bg-blue-600 hover:bg-blue-700 whitespace-nowrap"
          >
            New Company
          </Button>
        </Box>
      </Box>

      {/* Search Loading Indicator */}
      {searchLoading && results.length === 0 && (
        <Paper elevation={2} sx={{ p: 4, mb: 3 }}>
          <LoadingSpinner message="Searching companies..." />
        </Paper>
      )}

      {/* Search Results - Show only if we have results */}
      {results.length > 0 && (
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>
            Search Results
          </Typography>

          {searchError && <ErrorMessage message={searchError} />}

          {!searchError && (
            <EntitySearchResults
              results={results}
              onSelect={handleSelectCompany}
              renderItem={renderCompanyItem}
            />
          )}
        </Paper>
      )}

      {/* Main Form Section */}
      <Paper elevation={2} className="p-6">
        <Typography variant="h5" className="mb-4 font-semibold text-gray-700 flex items-center gap-2">
          {selectedCompany ? (
            <>
              <BusinessIcon className="text-blue-600" />
              Edit Company: {selectedCompany.name}
            </>
          ) : (
            <>
              <AddIcon className="text-blue-600" />
              Create New Company
            </>
          )}
        </Typography>

        {loadingCompany && <LoadingSpinner message="Loading company..." />}

        {error && <ErrorMessage message={error} onDismiss={() => setError(null)} />}

        {!loadingCompany && (
          <CompanyForm
            company={selectedCompany}
            onSave={handleSave}
            onDelete={handleDelete}
            onCancel={handleNewCompany}
          />
        )}
      </Paper>
    </Container>
  );
};

export default CompaniesPage;
